// 【退役】夜職専門に絞ったため、この画面はルーティングから外してある（v4.2.0）。
// 夜職Proモード用 Laravel APIクライアント (easymenu-translation)
// Firebase(Firestore)とは完全独立。店舗アカウントトークンは localStorage に保持。
// Flutter版 night_pro_api_service.dart / RN版 NightclubService.ts と同一API契約。

const BASE_URL = 'https://easymenu-translation.com';

const KEY_TOKEN = 'np_user_token';
const KEY_STORE = 'np_store_info';

export class NightProAuthError extends Error {}

// 金額系は string | number 混在で返るため防御的にパース
export const npNum = (v: unknown): number => {
  if (v == null) return 0;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : 0;
};

export const npYen = (v: number): string => `¥${Math.round(v).toLocaleString('ja-JP')}`;

/**
 * 営業日。
 *
 * 以前は 5 時固定だった。会計側は店舗設定 businessDayBoundaryTime、
 * シフトのガントは 6 固定、勤怠は区切りなしと、同一製品内に4通りの
 * 日付の切れ目があり、5:00 まで開ける店では同じ一夜がレポートごとに
 * 別の日に散っていた。店舗設定を受け取る形に揃える。
 *
 * @param boundaryHour 区切り時刻(0-23)。0/未指定なら 5 を使う（NightProの従来値）
 */
export const businessDate = (
  now: Date = new Date(),
  boundaryHour?: number
): string => {
  const boundary = boundaryHour && boundaryHour > 0 ? boundaryHour : 5;
  const d = new Date(now);
  if (d.getHours() < boundary) d.setDate(d.getDate() - 1);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

// 月範囲 (offset=0 が当月)
export const monthRange = (offset: number) => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const last = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: fmt(first), to: fmt(last), label: `${first.getFullYear()}年${first.getMonth() + 1}月` };
};

// ============ 型 (サーバー契約) ============
export interface NpStoreUser {
  id: number;
  name: string;
  email: string;
  store_name?: string;
  business_category?: string;
}

export interface NpAllowanceType {
  id: number;
  name: string;
  calc_type: 'fixed' | 'percent';
  amount: number | string;
  back_rate: number | string;
  show_on_receipt: boolean;
  is_active: boolean;
}

export interface NpOrderAllowance {
  id: number;
  cast_id: number | null;
  name: string;
  unit_amount: number | string;
  quantity: number;
  amount: number | string;
  back_amount: number | string;
  cast?: { id: number; name: string } | null;
}

export interface NpStaff {
  id: number;
  name: string;
  email: string;
  language?: string;
  role: 'staff' | 'cast' | 'manager';
  hourly_wage: number | string | null;
  daily_wage?: number | string | null;
  commission_rate: number | string | null;
  is_active: boolean;
  pay_type?: 'hourly' | 'daily';
  back_enabled?: boolean;
  commission_base?: 'orders' | 'items';
}

export const ROLE_LABEL: Record<string, string> = {
  cast: 'キャスト',
  staff: '黒服/スタッフ',
  manager: 'マネージャー',
};

export interface NpShift {
  id: number;
  staff_id: number;
  work_date: string;
  clock_in: string;
  clock_out: string | null;
  break_minutes: number;
  worked_minutes: number | null;
  base_pay: number | null;
  staff?: { id: number; name: string; role: string };
}

export interface NpPayrollRow {
  staff_id: number;
  name: string;
  role: string;
  shift_count: number;
  worked_minutes: number;
  worked_hours: number;
  hourly_wage: number | string;
  base_pay: number | string;
  back_pay: number | string;
  sales_total: number | string;
  commission_rate: number | string;
  commission: number | string;
  total_pay: number | string;
}

export interface NpAllowanceSummary {
  name: string;
  count: number;
  amount_total: number | string;
  back_total: number | string;
}

export interface NpCastReportRow {
  cast_id: number;
  name: string;
  role: string;
  order_count: number;
  sales_total: number | string;
  item_sales_total: number | string;
  allowances: NpAllowanceSummary[];
  allowance_amount_total: number | string;
  allowance_back_total: number | string;
}

export interface NpActiveOrder {
  id: number;
  table_number: string;
  total: number | string;
  cast_id: number | null;
  created_at: string;
}

// ============ 認証 ============
export const getStoredStore = (): NpStoreUser | null => {
  const token = localStorage.getItem(KEY_TOKEN);
  const info = localStorage.getItem(KEY_STORE);
  if (!token || !info) return null;
  try {
    return JSON.parse(info) as NpStoreUser;
  } catch {
    return null;
  }
};

export const npLogout = (): void => {
  localStorage.removeItem(KEY_TOKEN);
  localStorage.removeItem(KEY_STORE);
};

const baseHeaders = (token?: string): Record<string, string> => ({
  'Content-Type': 'application/json',
  Accept: 'application/json',
  'X-App-Type': 'web-dashboard',
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

export const npLogin = async (email: string, password: string): Promise<NpStoreUser> => {
  const res = await fetch(`${BASE_URL}/api/auth/login`, {
    method: 'POST',
    headers: baseHeaders(),
    body: JSON.stringify({ email, password, device_name: 'shop-dashboard Web NightPro' }),
  });
  const data = await res.json();
  if (!data.success || !data.token) {
    throw new Error(data.message || 'ログインに失敗しました');
  }
  localStorage.setItem(KEY_TOKEN, data.token);
  localStorage.setItem(KEY_STORE, JSON.stringify(data.user || {}));
  return data.user as NpStoreUser;
};

// ============ 内部共通 ============
const requireToken = (): string => {
  const token = localStorage.getItem(KEY_TOKEN);
  if (!token) throw new NightProAuthError('店舗アカウントでログインしてください');
  return token;
};

const adminFetch = async (
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown
): Promise<any> => {
  const token = requireToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: baseHeaders(token),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (res.status === 401) {
    npLogout();
    throw new NightProAuthError('セッションが切れました。再ログインしてください');
  }
  let data: any;
  try {
    data = await res.json();
  } catch {
    throw new Error(`サーバー応答を解析できませんでした (HTTP ${res.status})`);
  }
  if (!res.ok || data.success === false) {
    throw new Error(data.message || `エラーが発生しました (HTTP ${res.status})`);
  }
  return data;
};

// ============ 手当マスタ ============
export const getAllowanceTypes = async (): Promise<NpAllowanceType[]> =>
  (await adminFetch('GET', '/api/admin/allowance-types')).allowance_types ?? [];

export const createAllowanceType = (payload: Partial<NpAllowanceType>) =>
  adminFetch('POST', '/api/admin/allowance-types', payload);

export const updateAllowanceType = (id: number, payload: Partial<NpAllowanceType>) =>
  adminFetch('PUT', `/api/admin/allowance-types/${id}`, payload);

export const deleteAllowanceType = (id: number) =>
  adminFetch('DELETE', `/api/admin/allowance-types/${id}`);

// ============ アクティブ注文(卓) / 注文手当 ============
export const getActiveOrders = async (): Promise<NpActiveOrder[]> =>
  (await adminFetch('GET', '/api/admin/orders')).orders ?? [];

export const getOrderAllowances = async (
  orderId: number
): Promise<{ allowances: NpOrderAllowance[]; total_amount: number }> => {
  const data = await adminFetch('GET', `/api/admin/orders/${orderId}/allowances`);
  return { allowances: data.allowances ?? [], total_amount: npNum(data.total_amount) };
};

export const addOrderAllowance = (
  orderId: number,
  payload: { allowance_type_id: number; cast_id?: number | null; quantity?: number }
) => adminFetch('POST', `/api/admin/orders/${orderId}/allowances`, payload);

export const removeOrderAllowance = (orderId: number, allowanceId: number) =>
  adminFetch('DELETE', `/api/admin/orders/${orderId}/allowances/${allowanceId}`);

export const setOrderCast = (orderId: number, castId: number | null) =>
  adminFetch('POST', `/api/admin/orders/${orderId}/cast`, { cast_id: castId });

// ============ スタッフ ============
export const getNpStaff = async (): Promise<NpStaff[]> =>
  (await adminFetch('GET', '/api/admin/staff')).staff ?? [];

// 夜職フィールド更新。サーバー仕様で name/email/language は必須再送。
export const updateStaffNightFields = (
  base: NpStaff,
  fields: {
    role: string;
    pay_type: string;
    hourly_wage?: number;
    daily_wage?: number;
    commission_rate?: number;
    commission_base: string;
    back_enabled: boolean;
    is_active: boolean;
  }
) =>
  adminFetch('PUT', `/api/admin/staff/${base.id}`, {
    name: base.name,
    email: base.email,
    language: base.language || 'ja',
    ...fields,
  });

// ============ 打刻 ============
export const getShifts = async (from: string, to: string): Promise<NpShift[]> =>
  (await adminFetch('GET', `/api/admin/shifts?from=${from}&to=${to}`)).shifts ?? [];

export const clockIn = (staffId: number, workDate: string) =>
  adminFetch('POST', '/api/admin/shifts/clock-in', { staff_id: staffId, work_date: workDate });

export const clockOut = (shiftId: number) =>
  adminFetch('POST', `/api/admin/shifts/${shiftId}/clock-out`, {});

// ============ レポート ============
export const getPayroll = async (
  from: string,
  to: string
): Promise<{ payroll: NpPayrollRow[]; totals: Record<string, unknown> }> => {
  const data = await adminFetch('GET', `/api/admin/reports/payroll?from=${from}&to=${to}`);
  return { payroll: data.payroll ?? [], totals: data.totals ?? {} };
};

export const getCastReport = async (from: string, to: string): Promise<NpCastReportRow[]> =>
  (await adminFetch('GET', `/api/admin/reports/casts?from=${from}&to=${to}`)).report ?? [];
